import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Mail, CalendarDays, MessageSquare, Phone, MapPin } from 'lucide-react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { FAQ, FAQ_INTRO, CALENDLY, CONTACT_EMAIL, CONTACT_PHONE, CONTACT_PHONE_HREF, openCal, scrollToId } from './homeData';
import { getSource } from '../../lib/source';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SOLUTIONS = { 'lead-recovery': 'Lead Recovery System', 'booking-growth': 'Booking Growth System', 'multi-location': 'Multi-Location Operations System', 'not-sure': 'Not Sure—Help Me Choose' };
const INDUSTRIES = { hvac: 'HVAC', roofing: 'Roofing', plumbing: 'Plumbing', construction: 'Construction', landscaping: 'Landscaping', electrical: 'Electrical', dental: 'Dental', medspa: 'Med Spa', junk: 'Junk Removal', cleaning: 'Cleaning', auto: 'Auto Services', property: 'Property Management', professional: 'Professional Services', other: 'Other Service Business' };
const REVENUE = ['Under $50K', '$50K–$100K', '$100K–$250K', '$250K–$500K', '$500K–$1M', 'Over $1M', 'Prefer Not To Say'];
const VOLUME = ['Under 50', '50–150', '150–300', '300–750', '750+', 'Not Sure'];
const BUDGET = ['Under $5,000', '$5,000–$10,000', '$10,000–$25,000', '$25,000+', 'Not Sure Yet'];
const EMPTY = { fullName: '', companyName: '', email: '', phone: '', industry: '', website: '', monthlyRevenue: '', monthlyLeadVolume: '', challenge: '', solution: 'not-sure', budget: '', consentContact: false, consentSms: false, hp: '' };

const FaqContactCard = () => (
  <div className="yv-faq-card" data-testid="faq-contact-card">
    <MessageSquare className="w-5 h-5" aria-hidden="true" />
    <h3 className="yv-faq-card-title">Have A Different Question? <span className="yv-blue-ink">Let’s Talk.</span></h3>
    <p>Every business is different. Tell us how you operate, and we will confirm fit during a consultation.</p>
    <div className="yv-faq-card-actions">
      <button onClick={() => scrollToId('contact')} className="yv-btn yv-btn-primary yv-btn-sm" data-testid="faq-apply-btn">Apply For A Consultation <ArrowRight className="w-4 h-4" aria-hidden="true" /></button>
    </div>
    <div className="yv-faq-card-links">
      <a href={CONTACT_PHONE_HREF} className="yv-faq-card-mail" data-testid="faq-phone-link"><Phone className="w-4 h-4" aria-hidden="true" />{CONTACT_PHONE}</a>
      <a href={`mailto:${CONTACT_EMAIL}`} className="yv-faq-card-mail yv-keep-case" data-testid="faq-email-link"><Mail className="w-4 h-4" aria-hidden="true" />{CONTACT_EMAIL}</a>
    </div>
  </div>
);

export const HomeFAQ = () => (
  <section id="faq" className="yv-section yv-dark yv-grid-dark" data-testid="faq-section">
    <div className="yv-container yv-faq-grid">
      <div className="yv-faq-side">
        <p className="yv-eyebrow">FAQ</p>
        <h2 className="yv-h2" data-testid="faq-heading">Your Business. Your Systems. <span className="yv-blue-ink">Your Questions, Answered.</span></h2>
        <p className="yv-lede mt-5" data-testid="faq-intro">{FAQ_INTRO}</p>
        <FaqContactCard />
      </div>
      <AccordionPrimitive.Root type="single" collapsible defaultValue="q0" className="yv-faq2" data-testid="faq-list">
        {FAQ.map((f, i) => (
          <AccordionPrimitive.Item key={f.q} value={`q${i}`} className="yv-faq2-item" data-testid={`faq-item-${i}`}>
            <AccordionPrimitive.Header asChild>
              <h3>
                <AccordionPrimitive.Trigger className="yv-faq2-q" data-testid={`faq-trigger-${i}`}>
                  <span className="yv-faq2-n yv-mono">{String(i + 1).padStart(2, '0')}</span>
                  <span className="yv-faq2-text">{f.q}</span>
                  <span className="yv-faq2-icon" aria-hidden="true" />
                </AccordionPrimitive.Trigger>
              </h3>
            </AccordionPrimitive.Header>
            <AccordionPrimitive.Content className="yv-faq2-a" data-testid={`faq-answer-${i}`}>
              <p>{f.a}</p>
            </AccordionPrimitive.Content>
          </AccordionPrimitive.Item>
        ))}
      </AccordionPrimitive.Root>
    </div>
  </section>
);

const validate = (f) => {
  const e = {};
  if (f.fullName.trim().length < 2) e.fullName = 'Please Enter Your Full Name';
  if (!f.companyName.trim()) e.companyName = 'Please Enter Your Company Name';
  if (!EMAIL_RE.test(f.email.trim())) e.email = 'Please Enter A Valid Business Email';
  if (f.phone.replace(/\D/g, '').length < 7) e.phone = 'Please Enter A Valid Phone Number';
  if (!f.industry) e.industry = 'Please Select Your Industry';
  if (f.challenge.trim().length < 5) e.challenge = 'Please Describe Your Biggest Challenge';
  if (!f.consentContact) e.consentContact = 'Please Confirm We May Contact You';
  return e;
};

const Field = ({ id, label, error, children, wide }) => (
  <div className={`yv-field ${wide ? 'yv-field-wide' : ''}`}>
    <label htmlFor={id} className="yv-field-label">{label}</label>
    {children}
    {error && <p className="yv-field-err" id={`${id}-err`} data-testid={`${id}-error`}>{error}</p>}
  </div>
);

const Select = ({ id, value, onChange, options, placeholder }) => (
  <select id={id} className="yv-input" value={value} onChange={onChange} data-testid={id}>
    {placeholder && <option value="">{placeholder}</option>}
    {Array.isArray(options) ? options.map((o) => <option key={o} value={o}>{o}</option>) : Object.entries(options).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

const useApplication = () => {
  const navigate = useNavigate();
  const started = useRef(Date.now());
  const [form, setForm] = useState(() => {
    const pending = sessionStorage.getItem('yv-industry');
    sessionStorage.removeItem('yv-industry');
    return pending && INDUSTRIES[pending] ? { ...EMPTY, industry: pending } : EMPTY;
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  useEffect(() => {
    const onInd = (e) => INDUSTRIES[e.detail] && setForm((f) => ({ ...f, industry: e.detail }));
    const onPkg = (e) => SOLUTIONS[e.detail] && setForm((f) => ({ ...f, solution: e.detail }));
    window.addEventListener('yv:industry', onInd);
    window.addEventListener('yv:package', onPkg);
    return () => { window.removeEventListener('yv:industry', onInd); window.removeEventListener('yv:package', onPkg); };
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) { document.getElementById(`app-${Object.keys(errs)[0]}`)?.focus(); return; }
    setStatus('sending');
    try {
      const { data } = await axios.post(`${API}/applications`, {
        ...form, industry: INDUSTRIES[form.industry], solution: SOLUTIONS[form.solution],
        source: getSource(), elapsedMs: Date.now() - started.current,
      });
      sessionStorage.setItem('yv-app', JSON.stringify({ id: data.id, token: data.token, name: form.fullName, email: form.email }));
      navigate('/application-received');
    } catch (err) {
      setStatus(err.response?.status === 429 ? 'limited' : 'error');
    }
  };
  return { form, errors, status, set, submit };
};

const ApplicationForm = () => {
  const { form, errors, status, set, submit } = useApplication();
  const input = (k, props = {}) => (
    <input id={`app-${k}`} className="yv-input" value={form[k]} onChange={set(k)} aria-invalid={!!errors[k]} data-testid={`app-${k}`} {...props} />
  );
  return (
    <form onSubmit={submit} noValidate className="yv-form yv-app-form" data-testid="application-form">
      <div className="yv-form-head"><span className="yv-mono">Consultation Application</span><span className="yv-mono yv-muted-ink">* Required</span></div>
      <div className="yv-app-grid">
        <Field id="app-fullName" label="Full Name *" error={errors.fullName}>{input('fullName', { autoComplete: 'name' })}</Field>
        <Field id="app-companyName" label="Company Name *" error={errors.companyName}>{input('companyName', { autoComplete: 'organization' })}</Field>
        <Field id="app-email" label="Business Email *" error={errors.email}>{input('email', { type: 'email', autoComplete: 'email' })}</Field>
        <Field id="app-phone" label="Phone Number *" error={errors.phone}>{input('phone', { type: 'tel', autoComplete: 'tel' })}</Field>
        <Field id="app-industry" label="Industry *" error={errors.industry}><Select id="app-industry" value={form.industry} onChange={set('industry')} options={INDUSTRIES} placeholder="Select Industry" /></Field>
        <Field id="app-website" label="Company Website">{input('website', { type: 'url', placeholder: 'https://', autoComplete: 'url' })}</Field>
        <Field id="app-monthlyRevenue" label="Approximate Monthly Revenue"><Select id="app-monthlyRevenue" value={form.monthlyRevenue} onChange={set('monthlyRevenue')} options={REVENUE} placeholder="Select Range" /></Field>
        <Field id="app-monthlyLeadVolume" label="Approximate Monthly Lead Volume"><Select id="app-monthlyLeadVolume" value={form.monthlyLeadVolume} onChange={set('monthlyLeadVolume')} options={VOLUME} placeholder="Select Range" /></Field>
        <Field id="app-solution" label="Interested YOVAI Solution"><Select id="app-solution" value={form.solution} onChange={set('solution')} options={SOLUTIONS} /></Field>
        <Field id="app-budget" label="Estimated Implementation Budget"><Select id="app-budget" value={form.budget} onChange={set('budget')} options={BUDGET} placeholder="Select Range" /></Field>
        <Field id="app-challenge" label="Biggest Operational Challenge *" error={errors.challenge} wide>
          <textarea id="app-challenge" className="yv-input yv-textarea" value={form.challenge} onChange={set('challenge')} maxLength={3000} aria-invalid={!!errors.challenge} data-testid="app-challenge" />
        </Field>
      </div>
      <input type="text" name="company_url_confirm" value={form.hp} onChange={set('hp')} tabIndex={-1} autoComplete="off" aria-hidden="true" className="yv-hp" />
      <label className="yv-check" data-testid="app-consent-contact-label">
        <input type="checkbox" id="app-consentContact" checked={form.consentContact} onChange={set('consentContact')} data-testid="app-consent-contact" />
        <span>I Agree That YOVAI May Contact Me By Email Or Phone About This Application. *</span>
      </label>
      {errors.consentContact && <p className="yv-field-err" data-testid="app-consentContact-error">{errors.consentContact}</p>}
      <label className="yv-check" data-testid="app-consent-sms-label">
        <input type="checkbox" checked={form.consentSms} onChange={set('consentSms')} data-testid="app-consent-sms" />
        <span>Optional: I Agree To Receive Text Messages From YOVAI About My Application. Message Frequency Varies. Message And Data Rates May Apply. Reply STOP To Opt Out. Consent Is Not A Condition Of Purchase.</span>
      </label>
      <button type="submit" disabled={status === 'sending'} className="yv-btn yv-btn-primary yv-btn-block" data-testid="app-submit-btn">
        {status === 'sending' ? 'Submitting…' : 'Submit Application'} <ArrowRight className="w-4 h-4" aria-hidden="true" />
      </button>
      <div aria-live="polite">
        {status === 'error' && <p className="yv-field-err" data-testid="app-error">Something Went Wrong. Please Try Again Or Email {CONTACT_EMAIL}.</p>}
        {status === 'limited' && <p className="yv-field-err" data-testid="app-limited">We Received Several Applications From This Connection. Please Email {CONTACT_EMAIL}.</p>}
      </div>
      <p className="yv-fine">
        Your Information Is Used Only To Review Your Application And Contact You. See Our{' '}
        <Link to="/privacy" className="yv-link">Privacy Policy</Link>, <Link to="/sms-terms" className="yv-link">SMS Terms</Link>, And <Link to="/terms" className="yv-link">Terms Of Service</Link>.
      </p>
    </form>
  );
};

export const ContactSection = () => (
  <section id="contact" className="yv-section yv-dark yv-grid-dark" data-testid="contact-section">
    <div className="yv-container yv-contact-grid">
      <div>
        <p className="yv-eyebrow">Apply For A Consultation</p>
        <h2 className="yv-h2" data-testid="final-headline">Infrastructure Designed Around <span className="yv-blue-ink">The Way Your Business Operates.</span></h2>
        <p className="yv-lede mt-5" data-testid="final-copy">YOVAI Designs And Implements Connected Communication, CRM, And Automation Systems That Help Growing Businesses Capture Opportunities, Streamline Operations, And Operate With Greater Clarity.</p>
        <div className="yv-contact-list">
          <a href={CONTACT_PHONE_HREF} className="yv-contact-row" data-testid="contact-phone-link">
            <Phone className="w-5 h-5" aria-hidden="true" /><span><b>Call</b><span className="yv-contact-sub">{CONTACT_PHONE}</span></span>
          </a>
          <a href={`mailto:${CONTACT_EMAIL}`} className="yv-contact-row" data-testid="contact-email-link">
            <Mail className="w-5 h-5" aria-hidden="true" /><span><b>Email</b><span className="yv-keep-case yv-contact-sub">{CONTACT_EMAIL}</span></span>
          </a>
          <button onClick={openCal} className="yv-contact-row" data-testid="contact-calendly-btn">
            <CalendarDays className="w-5 h-5" aria-hidden="true" /><span><b>Already Applied? Schedule Your Discovery Call</b><span className="yv-keep-case yv-contact-sub">{CALENDLY.replace('https://', '')}</span></span>
          </button>
          <div className="yv-contact-row is-static"><MapPin className="w-5 h-5" aria-hidden="true" /><span><b>Atlanta-Based</b><span className="yv-contact-sub">Serving Businesses Nationwide</span></span></div>
        </div>
      </div>
      <ApplicationForm />
    </div>
  </section>
);
